import React, { useEffect, useState } from "react";
import { createEmployee, getAllEmployees, resetPasswordOtpApi, sendResetOtpApi, toggleEmployeeStatusApi, updateEmployeeApi } from "../../Api/adminApi";
import toast from "react-hot-toast";
import { Search, Plus, Eye, EyeOff, X, Trash2, Edit2, Users, CheckCircle, Lock } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

 
const Employee = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({ active: 0, inactive: 0 });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [resetEmployee, setResetEmployee] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  const itemsPerPage = 10;

  const [form, setForm] = useState({
    name: "",
    designation: "",
    mobile: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const [editForm, setEditForm] = useState({
    name: "",
    designation: "",
    mobile: "",
    username: "",
    email: "",
  });

  const [editFormErrors, setEditFormErrors] = useState({});

  const [resetFormData, setResetFormData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [resetFormErrors, setResetFormErrors] = useState({});

  const exportToExcel = () => {
    if (!employees || employees.length === 0) {
      toast.error("No data to export");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // All Employees Sheet
    const allData = employees.map((emp, index) => ({
      "S.No": index + 1,
      Name: emp.name || "",
      Designation: emp.designation || "",
      Email: emp.email || "",
      Mobile: emp.mobile || "",
      Username: emp.username || "",
      Status: emp.isActive ? "Active" : "Inactive",
    }));
    
    const allWorksheet = XLSX.utils.json_to_sheet(allData);
    XLSX.utils.book_append_sheet(workbook, allWorksheet, "All Employees");

    // Active Employees Sheet
    const activeEmployees = employees.filter(emp => emp.isActive);
    const activeData = activeEmployees.map((emp, index) => ({
      "S.No": index + 1,
      Name: emp.name || "",
      Designation: emp.designation || "",
      Email: emp.email || "",
      Mobile: emp.mobile || "",
      Username: emp.username || "",
    }));
    
    const activeWorksheet = XLSX.utils.json_to_sheet(activeData);
    XLSX.utils.book_append_sheet(workbook, activeWorksheet, "Active Employees");

    // Inactive Employees Sheet
    const inactiveEmployees = employees.filter(emp => !emp.isActive);
    const inactiveData = inactiveEmployees.map((emp, index) => ({
      "S.No": index + 1,
      Name: emp.name || "",
      Designation: emp.designation || "",
      Email: emp.email || "",
      Mobile: emp.mobile || "",
      Username: emp.username || "",
    }));
    
    const inactiveWorksheet = XLSX.utils.json_to_sheet(inactiveData);
    XLSX.utils.book_append_sheet(workbook, inactiveWorksheet, "Inactive Employees");

    // Auto-size columns
    const colWidths = [
      { wch: 6 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }
    ];
    
    [allWorksheet, activeWorksheet, inactiveWorksheet].forEach(ws => {
      ws['!cols'] = colWidths;
    });

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(fileData, `Employees_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("✅ Excel exported with Active & Inactive sheets!");
  };

  const validateForm = () => {
    const errors = {};
    
    if (!form.name.trim()) errors.name = "Full name is required";
    if (!form.designation.trim()) errors.designation = "Designation is required";
    if (!form.mobile.trim()) errors.mobile = "Mobile number is required";
    if (!form.username.trim()) errors.username = "Username is required";
    if (!form.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Please enter a valid email";
    
    if (!form.password) errors.password = "Password is required";
    else if (form.password.length < 8) errors.password = "Password must be at least 8 characters";
    
    if (form.password !== form.confirmPassword) errors.confirmPassword = "Passwords do not match";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.name.trim()) errors.name = "Full name is required";
    if (!editForm.designation.trim()) errors.designation = "Designation is required";
    if (!editForm.mobile.trim()) errors.mobile = "Mobile number is required";
    if (!editForm.username.trim()) errors.username = "Username is required";
    if (!editForm.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) errors.email = "Please enter a valid email";

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateResetForm = () => {
    const errors = {};
    
    if (!resetFormData.otp.trim()) errors.otp = "OTP is required";
    if (!resetFormData.newPassword) errors.newPassword = "New password is required";
    else if (resetFormData.newPassword.length < 8) errors.newPassword = "Password must be at least 8 characters";
    
    if (resetFormData.newPassword !== resetFormData.confirmPassword) 
      errors.confirmPassword = "Passwords do not match";

    setResetFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (loading) return; // block double-submit

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    const payload = {
      ...form,
      name: form.name.trim(),
      designation: form.designation.trim(),
      mobile: form.mobile.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
    };
    const onCreated = () => {
      toast.success("✓ Employee created successfully!");
      fetchEmployees();
      setIsOpen(false);
      resetForm();
    };

    const knownIds = new Set(employees.map((emp) => String(emp._id)));

    setLoading(true);
    try {
      const res = await createEmployee(payload);

      if (res?.success) {
        onCreated();
      } else {
        toast.error(res?.message || "Could not create employee");
      }
    } catch (err) {
      console.error("Create employee error:", err?.response?.status, err?.response?.data || err);
      // The server can save the employee and still answer with an error
      // (e.g. a failing welcome email). Check the real list before reporting failure.
      let created = false;
      try {
        const list = await getAllEmployees();
        // a record that did not exist before this submit = the create went through
        created = !!list?.success && list.data.some((emp) => !knownIds.has(String(emp._id)));
      } catch (_) {
        /* fall through to the error toast */
      }

      if (created) {
        onCreated();
      } else {
        const status = err?.response?.status;
        toast.error(
          err?.response?.data?.message ||
            (!err?.response
              ? "Network error — please check your connection"
              : `Error creating employee${status ? ` (${status})` : ""}`)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await toggleEmployeeStatusApi(id);
      if (res.success) {
        toast.success(res.message);
        fetchEmployees();
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      designation: "",
      mobile: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.mobile?.includes(searchTerm)
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await getAllEmployees();
      if (res.success) {
        setEmployees(res.data);
        
        const activeCount = res.data.filter(emp => emp.isActive).length;
        const inactiveCount = res.data.length - activeCount;
        
        setStats({ 
          active: activeCount, 
          inactive: inactiveCount 
        });
      }
    } catch (error) {
      console.log("Fetch employee error:", error);
    }
  };

  const openEditModal = (emp) => {
    setEditEmployee(emp);
    setEditForm({
      name: emp.name || "",
      designation: emp.designation || "",
      mobile: emp.mobile || "",
      username: emp.username || "",
      email: emp.email || "",
    });
    setEditFormErrors({});
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);

      const res = await updateEmployeeApi(editEmployee._id, editForm);

      if (res.success) {
        toast.success(res.message || "Employee updated successfully");
        setEditEmployee(null);
        fetchEmployees();
      } else {
        toast.error(res.message || "Failed to update employee");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update employee");
    } finally {
      setLoading(false);
    }
  };

  const openResetModal = (emp) => {
    setResetEmployee(emp);
    setResetModalOpen(true);
    setOtpSent(false);
    setResetFormData({
      email: emp.email || "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
    setResetFormErrors({});
    setShowResetPassword(false);
    setShowResetConfirmPassword(false);
  };

  const handleSendOtp = async () => {
    if (!resetFormData.email) {
      toast.error("Email is required");
      return;
    }

    try {
      setResetLoading(true);
      const res = await sendResetOtpApi({ email: resetFormData.email });

      if (res.success) {
        toast.success(res.message || "OTP sent successfully");
        setOtpSent(true);
      } else {
        toast.error(res.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send OTP");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validateResetForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setResetLoading(true);

      const res = await resetPasswordOtpApi(resetFormData);

      if (res.success) {
        toast.success(res.message || "Password reset successful");
        setResetEmployee(null);
        setResetModalOpen(false);
        setOtpSent(false);
        setResetFormData({
          email: "",
          otp: "",
          newPassword: "",
          confirmPassword: "",
        });
        fetchEmployees();
      } else {
        toast.error(res.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        
        body {
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .gradient-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%);
          border: 1px solid rgba(99, 102, 241, 0.1);
          backdrop-filter: blur(10px);
        }

        .gradient-text {
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .fade-in {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }

        .fade-in-delay-1 { animation-delay: 0.1s; }
        .fade-in-delay-2 { animation-delay: 0.2s; }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .peer:checked:after {
          border-width: 2px;
        }

        .peer:hover {
          transform: scale(1.05);
        }

        @keyframes ripple {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(1.2); opacity: 0; }
        }

        .peer:focus-visible + div {
          position: relative;
        }

        .peer:focus-visible + div::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          margin: -10px 0 0 -10px;
          background: rgba(6, 182, 212, 0.3);
          border-radius: 50%;
          animation: ripple 0.6s linear;
        }

        .table-row-hover {
          transition: all 0.3s ease;
        }

        .table-row-hover:hover {
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.08) 0%, transparent 100%);
          transform: translateX(4px);
        }

        .search-input {
          transition: all 0.3s ease;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .search-input:focus {
          box-shadow: 0 0 20px rgba(6, 182, 212, 0.2), 0 0 40px rgba(99, 102, 241, 0.1);
          background: white;
        }

        .modal-backdrop {
          animation: backdropFadeIn 0.3s ease-out;
        }

        @keyframes backdropFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          animation: modalSlideUp 0.4s ease-out;
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .button-hover {
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px rgba(6, 182, 212, 0.25);
        }

        .header-gradient {
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%);
        }

        .stat-card {
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%);
          border-radius: 16px;
        }

        .input-focus {
          transition: all 0.3s ease;
        }

        .input-focus:focus {
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.2);
        }
      `}</style>

      {/* Header */}
      <div className="header-gradient border-b border-indigo-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-between items-start">
            <div className="fade-in">
              <h1 className="text-5xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                Employees
              </h1>
              <p className="text-gray-600 text-lg">Manage and organize your team members</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportToExcel}
                className="button-hover flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl"
              >
                Export Excel
              </button>

              <button
                onClick={() => {
                  setIsOpen(true);
                  resetForm();
                }}
                className="button-hover flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white px-6 py-3 rounded-xl"
              >
                <Plus size={20} />
                Add Employee
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-1 border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Active Employees</p>
                <p className="text-4xl font-bold text-emerald-600">{stats.active}</p>
                <p className="text-xs text-gray-400 mt-2">Currently working</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-emerald-600" size={28} />
              </div>
            </div>
          </div>

          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-2 border-l-4 border-l-red-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Inactive Employees</p>
                <p className="text-4xl font-bold text-red-600">{stats.inactive}</p>
                <p className="text-xs text-gray-400 mt-2">Deactivated members</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-rose-100 rounded-lg flex items-center justify-center">
                <EyeOff className="text-red-600" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="gradient-card rounded-2xl p-6 mb-6 fade-in shadow-sm">
          <div className="relative">
            <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, mobile or designation..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-300"
            />
          </div>
        </div>

        {/* Results Counter */}
        <div className="mb-4 flex items-center justify-between fade-in">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-cyan-600">{currentEmployees.length}</span> of <span className="font-semibold text-cyan-600">{filteredEmployees.length}</span> employees
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
            >
              Clear search
            </button>
          )}
        </div>

        {/* Table */}
        <div className="gradient-card rounded-2xl overflow-hidden fade-in shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              
              {/* HEADER */}
              <thead className="bg-gradient-to-r from-indigo-50 to-cyan-50 border-b border-indigo-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Designation</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Mobile</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>

              {/* BODY */}
              <tbody className="divide-y divide-gray-100">
                {currentEmployees.length > 0 ? (
                  currentEmployees.map((emp) => (
                    <tr key={emp._id} className="table-row-hover">

                      {/* NAME */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-600 text-white flex items-center justify-center font-bold">
                            {emp.name?.charAt(0)}
                          </div>
                          <span className="font-semibold text-gray-900">
                            {emp.name}
                          </span>
                        </div>
                      </td>

                      {/* DESIGNATION */}
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                          {emp.designation}
                        </span>
                      </td>

                      {/* MOBILE */}
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {emp.mobile}
                      </td>

                      {/* STATUS */}
                      <td className="px-6 py-4 text-center">
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 ${
                          emp.isActive 
                            ? "bg-green-100 text-green-800 shadow-lg shadow-green-200/50" 
                            : "bg-red-100 text-red-800 shadow-lg shadow-red-200/50"
                        }`}>
                          <div className={`w-2 h-2 rounded-full mr-2 transition-transform duration-300 ${
                            emp.isActive ? "bg-green-500 scale-110" : "bg-red-500"
                          }`}></div>
                          {emp.isActive ? "Active" : "Inactive"}
                        </div>
                      </td>

                      {/* ACTIONS */}
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-2">
                          {/* VIEW */}
                          <button
                            onClick={() => setViewEmployee(emp)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-cyan-50 text-cyan-600 hover:bg-cyan-100 transition-all duration-200 hover:scale-105"
                            title="View Details"
                          >
                            <Eye size={16} />
                            View
                          </button>

                          {/* EDIT */}
                          <button
                            onClick={() => openEditModal(emp)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-all duration-200 hover:scale-105"
                            title="Edit Employee"
                          >
                            <Edit2 size={16} />
                            Edit
                          </button>

                          {/* RESET PASSWORD */}
                          <button
                            onClick={() => openResetModal(emp)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition-all duration-200 hover:scale-105"
                            title="Reset Password"
                          >
                            <Lock size={16} />
                            Reset
                          </button>

                          {/* TOGGLE SWITCH */}
                          <label className="relative inline-flex items-center cursor-pointer group ml-2">
                            <input
                              type="checkbox"
                              checked={emp.isActive}
                              onChange={() => handleToggle(emp._id)}
                              className="sr-only peer"
                            />
                            
                            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-green-500 peer-checked:to-emerald-600 duration-300 transform peer-checked:scale-[1.05] shadow-md hover:shadow-lg group-hover:shadow-cyan-200/50"></div>
                            
                            <span className="ml-2 text-xs font-semibold transition-colors duration-300 group-hover:scale-105">
                              {emp.isActive ? "ON" : "OFF"}
                            </span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <Search className="text-gray-300 mb-3" size={48} />
                        <p className="text-gray-600 text-lg font-semibold">
                          No employees found
                        </p>
                        <p className="text-gray-400 text-sm mt-1">
                          {searchTerm
                            ? "Try adjusting your search"
                            : "Create your first employee"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between fade-in">
            <div className="text-sm text-gray-600">
              Page <span className="font-semibold text-cyan-600">{currentPage}</span> of <span className="font-semibold text-cyan-600">{totalPages}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Previous
              </button>

              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-cyan-500 to-indigo-600 text-white shadow-lg shadow-cyan-500/30"
                          : "border border-gray-200 text-gray-700 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-md z-50 p-4 modal-backdrop">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl w-full max-w-2xl max-h-[90vh] shadow-2xl border border-indigo-100/50 overflow-hidden modal-content">
            {/* Header */}
            <div className="px-8 py-8 border-b border-indigo-100/50 sticky top-0 bg-white/100 backdrop-blur-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1">
                    Create New Employee
                  </h3>
                  <p className="text-gray-500 text-sm">Fill all details to add a new team member</p>
                </div>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                  }}
                  className="p-2 rounded-2xl text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 group"
                >
                  <X size={24} className="group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleCreate} className="p-8 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Full Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter full name"
                      className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                        formErrors.name 
                          ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    {formErrors.name && (
                      <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <X size={14} />
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Designation <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g., Software Engineer"
                      className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                        formErrors.designation 
                          ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      value={form.designation}
                      onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    />
                    {formErrors.designation && (
                      <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <X size={14} />
                        {formErrors.designation}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Mobile Number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                        formErrors.mobile 
                          ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    />
                    {formErrors.mobile && (
                      <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <X size={14} />
                        {formErrors.mobile}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Username <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="johndoe"
                      className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                        formErrors.username 
                          ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                    />
                    {formErrors.username && (
                      <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <X size={14} />
                        {formErrors.username}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900 tracking-wide">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    placeholder="john@example.com"
                    className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                      formErrors.email 
                        ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                  {formErrors.email && (
                    <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                      <X size={14} />
                      {formErrors.email}
                    </p>
                  )}
                </div>

                {/* Password Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Minimum 8 characters"
                        className={`input-focus w-full pl-12 pr-12 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                          formErrors.password 
                            ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-3 flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-all duration-200 hover:scale-110"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                      {form.password && (
                        <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              form.password.length < 6
                                ? "w-1/4 bg-red-400"
                                : form.password.length < 8
                                ? "w-1/2 bg-yellow-400"
                                : "w-full bg-green-500"
                            }`}
                          />
                        </div>
                      )}
                    </div>
                    {formErrors.password && (
                      <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <X size={14} />
                        {formErrors.password}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Confirm Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        className={`input-focus w-full pl-12 pr-12 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all duration-300 text-sm placeholder-gray-400 ${
                          formErrors.confirmPassword 
                            ? "border-red-400 bg-red-50 shadow-sm shadow-red-200/50" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        value={form.confirmPassword}
                        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-3 flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-all duration-200 hover:scale-110"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    {formErrors.confirmPassword && (
                      <p className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <X size={14} />
                        {formErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-4 pt-8 mt-8 border-t border-indigo-100">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                  }}
                  className="flex-1 px-6 py-3.5 border-2 border-gray-200 rounded-2xl text-gray-700 font-semibold hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200/50 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading || Object.keys(formErrors).length > 0}
                  className="flex-1 px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-600 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl hover:-translate-y-1 disabled:shadow-none disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-cyan-500/50"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full shadow-md"></span>
                      Creating Employee...
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Create Employee
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewEmployee && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm z-50 p-4 modal-backdrop">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-indigo-100 modal-content">
            <div className="flex justify-between items-center px-6 py-6 border-b border-indigo-100 sticky top-0 bg-white/95 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-gray-900">Employee Details</h3>
              <button
                onClick={() => setViewEmployee(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-2 gap-6">
              <div className="gradient-card rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wider">Full Name</p>
                <p className="text-lg font-bold text-gray-900">{viewEmployee.name}</p>
              </div>

              <div className="gradient-card rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wider">Designation</p>
                <p className="text-lg font-bold gradient-text">{viewEmployee.designation}</p>
              </div>

              <div className="col-span-2 gradient-card rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wider">Email</p>
                <p className="text-sm text-gray-700 break-all font-medium">{viewEmployee.email}</p>
              </div>

              <div className="gradient-card rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wider">Mobile</p>
                <p className="text-sm text-gray-700 font-medium">{viewEmployee.mobile}</p>
              </div>

              <div className="gradient-card rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wider">Username</p>
                <p className="text-sm text-gray-700 font-medium">{viewEmployee.username}</p>
              </div>

              <div className="col-span-2 flex gap-2 pt-4 border-t border-indigo-100">
                <button
                  onClick={() => setViewEmployee(null)}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white rounded-lg font-semibold transition-all shadow-lg shadow-cyan-500/30"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editEmployee && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-md z-50 p-4 modal-backdrop">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl border border-indigo-100 overflow-hidden modal-content">
            <div className="px-8 py-6 border-b border-indigo-100 flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Edit Employee</h3>
                <p className="text-sm text-gray-500 mt-1">Update employee details</p>
              </div>
              <button
                onClick={() => setEditEmployee(null)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-800 hover:bg-gray-100"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleEditEmployee} className="p-8 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900 tracking-wide">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Full Name"
                    className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 ${
                      editFormErrors.name 
                        ? "border-red-400 bg-red-50" 
                        : "border-gray-200"
                    }`}
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                  {editFormErrors.name && (
                    <p className="text-red-500 text-xs font-medium">{editFormErrors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900 tracking-wide">Designation <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Designation"
                    className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 ${
                      editFormErrors.designation 
                        ? "border-red-400 bg-red-50" 
                        : "border-gray-200"
                    }`}
                    value={editForm.designation}
                    onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })}
                  />
                  {editFormErrors.designation && (
                    <p className="text-red-500 text-xs font-medium">{editFormErrors.designation}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900 tracking-wide">Mobile <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Mobile"
                    className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 ${
                      editFormErrors.mobile 
                        ? "border-red-400 bg-red-50" 
                        : "border-gray-200"
                    }`}
                    value={editForm.mobile}
                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                  />
                  {editFormErrors.mobile && (
                    <p className="text-red-500 text-xs font-medium">{editFormErrors.mobile}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900 tracking-wide">Username <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Username"
                    className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 ${
                      editFormErrors.username 
                        ? "border-red-400 bg-red-50" 
                        : "border-gray-200"
                    }`}
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  />
                  {editFormErrors.username && (
                    <p className="text-red-500 text-xs font-medium">{editFormErrors.username}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900 tracking-wide">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email"
                  className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-cyan-500/20 ${
                    editFormErrors.email 
                      ? "border-red-400 bg-red-50" 
                      : "border-gray-200"
                  }`}
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
                {editFormErrors.email && (
                  <p className="text-red-500 text-xs font-medium">{editFormErrors.email}</p>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setEditEmployee(null)}
                  className="flex-1 px-6 py-3.5 border-2 border-gray-200 rounded-2xl text-gray-700 font-semibold hover:border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-2xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Updating..." : "Update Employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModalOpen && resetEmployee && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-md z-50 p-4 modal-backdrop">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl border border-indigo-100 overflow-hidden modal-content">
            <div className="px-8 py-6 border-b border-indigo-100 flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Reset Password</h3>
                <p className="text-sm text-gray-500 mt-1">Update {resetEmployee.name}'s password</p>
              </div>
              <button
                onClick={() => {
                  setResetModalOpen(false);
                  setResetEmployee(null);
                }}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-800 hover:bg-gray-100"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="p-8 space-y-5">
              {/* Email Field - Disabled */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900 tracking-wide">Email Address</label>
                <input
                  type="email"
                  disabled
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-2xl bg-gray-50 text-gray-600 cursor-not-allowed"
                  value={resetFormData.email}
                />
              </div>

              {/* Send OTP */}
              {!otpSent ? (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={resetLoading}
                  className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg disabled:cursor-not-allowed"
                >
                  {resetLoading ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Lock size={18} />
                      Send OTP
                    </>
                  )}
                </button>
              ) : (
                <>
                  {/* OTP Field */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">OTP <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter OTP from email"
                      className={`input-focus w-full px-4 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 ${
                        resetFormErrors.otp 
                          ? "border-red-400 bg-red-50" 
                          : "border-gray-200"
                      }`}
                      value={resetFormData.otp}
                      onChange={(e) => setResetFormData({ ...resetFormData, otp: e.target.value })}
                    />
                    {resetFormErrors.otp && (
                      <p className="text-red-500 text-xs font-medium">{resetFormErrors.otp}</p>
                    )}
                  </div>

                  {/* New Password */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">New Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type={showResetPassword ? "text" : "password"}
                        placeholder="Minimum 8 characters"
                        className={`input-focus w-full pl-4 pr-12 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 ${
                          resetFormErrors.newPassword 
                            ? "border-red-400 bg-red-50" 
                            : "border-gray-200"
                        }`}
                        value={resetFormData.newPassword}
                        onChange={(e) => setResetFormData({ ...resetFormData, newPassword: e.target.value })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-3 flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                      >
                        {showResetPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {resetFormErrors.newPassword && (
                      <p className="text-red-500 text-xs font-medium">{resetFormErrors.newPassword}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900 tracking-wide">Confirm Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type={showResetConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter new password"
                        className={`input-focus w-full pl-4 pr-12 py-3.5 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 ${
                          resetFormErrors.confirmPassword 
                            ? "border-red-400 bg-red-50" 
                            : "border-gray-200"
                        }`}
                        value={resetFormData.confirmPassword}
                        onChange={(e) => setResetFormData({ ...resetFormData, confirmPassword: e.target.value })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-3 flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                      >
                        {showResetConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {resetFormErrors.confirmPassword && (
                      <p className="text-red-500 text-xs font-medium">{resetFormErrors.confirmPassword}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setResetModalOpen(false);
                        setResetEmployee(null);
                        setOtpSent(false);
                      }}
                      className="flex-1 px-6 py-3.5 border-2 border-gray-200 rounded-2xl text-gray-700 font-semibold hover:border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                   
                      type="submit"
                      disabled={resetLoading}
                      className="flex-1 px-6 py-3.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-2xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {resetLoading ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee;